import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaRocket, FaBrain, FaFileCode, FaExchangeAlt, FaLinkedin,
  FaDev, FaTwitter, FaMedium, FaReddit, FaGithub, FaYoutube,
  FaNewspaper, FaCheckCircle, FaArrowRight, FaMagic, FaGlobe,
  FaDownload, FaCode, FaPenFancy, FaUsers, FaCog, FaShieldAlt,
  FaBolt, FaInfinity, FaChartLine, FaLock
} from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import SeoHelmet from './SeoHelmet';
import MonoButton from '../kits/DesignKit/MonoButton';
import { ZenPostHeroSection, ZenPostWorkflowSection, ZenPostProblemSection } from './ZenPostSections';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';

const ZenPostStudioLandingPageHero = () => {
  const [activeStudio, setActiveStudio] = useState('content');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const trackCTA = (ctaName) => {
    if (window.gtag) {
      window.gtag('event', 'cta_click', {
        event_category: 'ZenPostStudio',
        event_label: ctaName,
      });
    }
    console.log(`CTA Clicked: ${ctaName}`);
  };

  const platforms = [
    { name: 'LinkedIn', icon: <FaLinkedin />, description: 'Business-Ton & klare Struktur' },
    { name: 'dev.to', icon: <FaDev />, description: 'Developer-Optimierung inklusive Tags' },
    { name: 'X (Twitter)', icon: <FaTwitter />, description: 'Threads mit Zeichen-Optimierung' },
    { name: 'Medium', icon: <FaMedium />, description: 'Publizieren mit sauberer Formatierung' },
    { name: 'Reddit', icon: <FaReddit />, description: 'TL;DR & Subreddit-Vorschläge' },
    { name: 'GitHub', icon: <FaGithub />, description: 'README- und Release-Content' },
    { name: 'YouTube', icon: <FaYoutube />, description: 'Titel, Beschreibung & Shorts-Teaser' },
    { name: 'Hashnode', icon: <FaNewspaper />, description: 'Developer-Blogs in Minuten' },
    { name: 'Notion', icon: <FaFileCode />, description: 'Sync für Docs und Wissensbasen' },
  ];

  const studios = {
    content: {
      title: 'Content AI Studio',
      icon: <FaPenFancy />,
      color: 'brand',
      description: 'Schreibe einmal. Publiziere auf 9+ Plattformen. Mit lokaler KI.',
      features: [
        'Multi-Platform Publishing (LinkedIn, dev.to, Medium, Twitter, Reddit, GitHub, YouTube, Hashnode, Notion)',
        'Tonalität anpassen (Professional, Casual, Technical, Marketing)',
        'Länge optimieren (Kurz, Medium, Lang)',
        'Zielgruppen-Anpassung (Developer, Business, General)',
        'Automatische Format-Konvertierung (Markdown, HTML, JSON)',
        '100% Offline mit Ollama/LLaMA Integration'
      ]
    },
    doc: {
      title: 'Doc Studio',
      icon: <FaFileCode />,
      color: 'blue',
      description: 'Dokumentation automatisch generieren. Für Entwickler gemacht.',
      features: [
        'README.md Generator mit Projekt-Analyse',
        'CHANGELOG.md aus Git-History',
        'API-Dokumentation aus Code',
        'Contributing Guidelines',
        'License Templates',
        'GitHub-ready Markdown'
      ]
    },
    converter: {
      title: 'Converter Studio',
      icon: <FaExchangeAlt />,
      color: 'green',
      description: 'Jedes Format in jedes Format. Verlustfrei.',
      features: [
        'EditorJS ↔ Markdown',
        'Markdown ↔ HTML',
        'Notion ↔ Markdown',
        'Word ↔ Markdown',
        'PDF Export',
        'JSON ↔ YAML'
      ]
    }
  };

  const useCases = [
    {
      icon: <FaCode className="text-4xl text-blue-600" />,
      title: 'Für Developer Relations',
      description: 'Schreibe einen Tech-Artikel, publiziere ihn mit einem Klick auf dev.to, Medium, Hashnode und GitHub. Generiere automatisch READMEs und Changelogs für deine Repos.',
      workflow: 'Write → Transform → Publish auf 9 Plattformen'
    },
    {
      icon: <FaPenFancy className="text-4xl text-brand" />,
      title: 'Für Content Creators',
      description: 'Erstelle einen LinkedIn-Post, wandle ihn mit KI in einen Twitter-Thread um, publiziere ihn als ausführlichen Medium-Artikel. Alles aus einem Tool.',
      workflow: 'LinkedIn → Twitter → Medium → YouTube (Description)'
    },
    {
      icon: <FaUsers className="text-4xl text-purple-600" />,
      title: 'Für Agenturen & Teams',
      description: 'Content-Teams arbeiten mit KI-Unterstützung 3x schneller. White-Label Lösung für eure Kunden. API-Zugriff für Custom Workflows.',
      workflow: 'Team Collaboration → Multi-Client Publishing'
    },
    {
      icon: <FaGlobe className="text-4xl text-green-600" />,
      title: 'Für Mehrsprachige Content Strategen',
      description: 'Schreibe auf Deutsch, übersetze mit lokaler KI in 50+ Sprachen, publiziere global. Reddit, Medium, LinkedIn – alle Märkte mit einem Tool.',
      workflow: 'DE → EN, ES, FR, IT → Global Publishing'
    }
  ];

  const pricingPlans = [
    {
      name: 'Desktop',
      price: 'Kostenlos',
      description: 'Die vollständige Desktop-App mit lokaler KI',
      features: [
        '🤖 Ollama/LLaMA Integration (lokal)',
        '✨ Content AI Studio',
        '📝 Doc Studio',
        '🔄 Converter Studio',
        '🚀 Multi-Platform Publishing',
        '💾 Lokale Datenspeicherung',
        '🔒 100% Offline',
        '⚡ Unbegrenzte Nutzung'
      ],
      cta: 'Desktop herunterladen',
      popular: false,
      ctaAction: () => {
        trackCTA('Pricing_Desktop_Download');
        window.open('https://github.com/denisbitter/zenpoststudio/releases', '_blank');
      }
    },
    {
      name: 'Pro',
      price: '39€',
      period: '/Monat',
      description: 'Desktop + Web + Cloud-Sync + Erweiterte KI',
      features: [
        'Alles aus Desktop',
        'Cloud-Sync über alle Geräte',
        'Team-Kollaboration (bis 5 User)',
        'Analytics & Insights',
        'Custom Templates',
        'Priority Support',
        'Versionierung & History',
        'API-Zugriff (Basic)'
      ],
      cta: 'Pro starten',
      popular: true,
      ctaAction: () => {
        trackCTA('Pricing_Pro_Start');
        window.location.href = 'mailto:saghallo@denisbitter.de?subject=ZenPost Studio Pro Plan';
      }
    },
    {
      name: 'Agency',
      price: '149€',
      period: '/Monat',
      description: 'White-Label + Unbegrenzte User + Custom Integration',
      features: [
        'Alles aus Pro',
        'Unbegrenzte Benutzer',
        'White-Label (Dein Branding)',
        '🔗 Full API-Zugriff',
        '🛠️ Custom Integrationen',
        '📞 Dedizierter Support',
        '🔐 SSO & Enterprise Security',
        '📊 Custom Analytics Dashboard'
      ],
      cta: 'Agency Demo',
      popular: false,
      ctaAction: () => {
        trackCTA('Pricing_Agency_Contact');
        window.location.href = 'mailto:saghallo@denisbitter.de?subject=ZenPost Studio Agency Plan';
      }
    }
  ];

  return (
    <>
      <SeoHelmet
        title="ZenPost Studio – Multi-Platform Content Publishing mit lokaler KI | Content AI + Doc Generator + Converter"
        description="Der erste Content Editor mit integrierter lokaler KI (Ollama/LLaMA). Schreibe einmal, publiziere auf 9+ Plattformen: LinkedIn, dev.to, Medium, Twitter, Reddit, GitHub, YouTube. Desktop App kostenlos."
        keywords="content publishing, multi platform, ai content editor, local ai, ollama, llama, developer relations, content automation, markdown editor, editorjs, documentation generator, zenpost studio"
        ogImage="/images/zenpoststudio-og.jpg"
      />

      <div
        className="min-h-screen text-white"
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: 'linear-gradient(180deg, #2a1035 0%, #141629 30%, #0b1020 100%)'
        }}
      >
        {/* Hero Section */}
        <ZenPostHeroSection
          eyebrow="AI-Powered Multi-Platform Publisher"
          title="Schreibe einmal."
          titleAccent="Publiziere überall."
          subtitle="Verwandle einen Artikel in Posts für 9+ Plattformen – LinkedIn, dev.to, Medium, X, Reddit, GitHub, YouTube, Hashnode & Notion."
          primaryCtaText="ZenPost Studio öffnen"
          onPrimaryCta={() => {
            trackCTA('Hero_ZenPostStudio');
            window.location.href = '/zenpost-studio';
          }}
          secondaryCtaText="Unterstützte Plattformen"
          onSecondaryCta={() => {
            document.getElementById('home-platforms')?.scrollIntoView({ behavior: 'smooth' });
          }}
          primaryUnderline="pink"
          secondaryUnderline="gray"
          stats={[
            { label: 'Plattformen', value: '9+' },
            { label: 'Lokale KI', value: 'Ollama' },
            { label: 'Offline', value: '100%' },
            { label: 'Zeit', value: 'Minuten' }
          ]}
        />

        <section id="home-platforms" className="py-12 sm:py-16 px-4 bg-[#0b1020]">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center rounded-full border border-slate-700/60 bg-slate-900/60 px-3 py-1 text-xs font-semibold text-slate-300">
              Supported Platforms
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-white">
              Ein Artikel, neun Plattformen
            </h2>
            <p className="mt-3 text-base sm:text-lg text-slate-300 max-w-3xl mx-auto">
              Jede Plattform bekommt Inhalte, die zu Ton, Länge und Format passen.
            </p>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 text-left">
              {platforms.map((platform, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-brand bg-slate-950/40 p-6 shadow-lg shadow-slate-950/40"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-800/80 text-brand text-xl">
                    {platform.icon}
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-white">
                    {platform.name}
                  </h3>
                  <p className="mt-2 text-sm text-slate-300">
                    {platform.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <ZenPostProblemSection
          title="Das bekannte Content-Problem"
          subtitle="Du schreibst einen Artikel. Dann copy-pastest du ihn auf LinkedIn. Dann formatierst du ihn für Medium. Dann kürzt du ihn für Twitter. Dann..."
          variant="content"
          usePaperBG={false}
          theme="dark"
          ctaText="Es gibt einen besseren Weg"
          onCTAClick={() => {
            trackCTA('Problem_Solution_Navigate');
            window.location.href = '/zenpost-studio';
          }}
        />

        {/* Solution Section - 3 Studios */}
    

        {/* Ollama/Local AI Section */}
     

        {/* Workflow Section */}
       

        {/* Use Cases Section */}
 

        {/* Pricing Section */}


        {/* FAQ Section */}
   

        {/* Final CTA Section */}
   
        {/* Footer */}
     

      </div>
    </>
  );
};

export default ZenPostStudioLandingPageHero;
